import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { apiRequest } from '@/config';
import { useAuth } from '@/contexts/AuthContext';

export function CreatePromptModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Budgeting');
  const [text, setText] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await apiRequest('/api/eval/prompts', {
        method: 'POST',
        body: JSON.stringify({
          name,
          category,
          text,
          notes,
          source: 'eval',
          user_email: user?.email,
        }),
      });

      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Create New Prompt" onClose={onClose} maxWidth="600px">
      <form onSubmit={handleSubmit}>
        {/* Name */}
        <div className="mb-5">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            className="w-full px-3 py-3 rounded-md text-base"
            style={{ border: '1px solid #E8E0D8', color: '#1A1A1A' }}
            placeholder="e.g., Simple Monthly Budget"
          />
        </div>

        {/* Category */}
        <div className="mb-5">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Category
          </label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="w-full px-3 py-3 rounded-md text-base bg-white"
            style={{ border: '1px solid #E8E0D8', color: '#1A1A1A' }}
          >
            <option value="Budgeting">Budgeting</option>
            <option value="Analytics">Analytics</option>
            <option value="Ops">Ops</option>
            <option value="Creative">Creative</option>
          </select>
        </div>

        {/* Prompt Text */}
        <div className="mb-5">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Prompt Text *
          </label>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            required
            rows={6}
            className="w-full px-3 py-3 rounded-md text-base font-inherit resize-y"
            style={{ border: '1px solid #E8E0D8', color: '#1A1A1A' }}
            placeholder="Create a spreadsheet that..."
          />
          <div className="text-xs mt-1" style={{ color: '#A0A0A0' }}>
            {text.length} characters
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-3 rounded-md text-base font-inherit resize-y"
            style={{ border: '1px solid #E8E0D8', color: '#1A1A1A' }}
            placeholder="What does this prompt test?"
          />
        </div>

        {error && (
          <div
            className="mb-4 p-3 rounded-md text-sm"
            style={{
              background: 'rgba(196, 30, 58, 0.08)',
              border: '1px solid #C41E3A',
              color: '#C41E3A',
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || !name.trim() || !text.trim()}
          >
            {submitting ? 'Creating...' : 'Create Prompt'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
